import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Youtube, Linkedin, MessageCircle, Globe, Mail, Phone, MapPin, Banknote, Truck, Send } from "lucide-react";
import api, { apiError } from "@/lib/api";
import logo from "@/assets/logo-togobati.png";
import { toast } from "sonner";

const ICONS = { facebook: Facebook, instagram: Instagram, twitter: Twitter, x: Twitter, youtube: Youtube, linkedin: Linkedin, whatsapp: MessageCircle };

const PAYMENTS = [
  { icon: Banknote, label: "Espèces à la livraison" },
  { icon: Truck, label: "Livraison Lomé & environs" },
];

function PaymentLogos() {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="footer-payments-logos">
      {/* Flooz */}
      <span className="inline-flex items-center h-9 px-3 rounded-md bg-white shadow-sm" data-testid="payment-flooz" title="Flooz">
        <span className="font-display font-extrabold italic text-[15px] text-[#F58220] tracking-tight">flooz</span>
      </span>
      {/* T-Money */}
      <span className="inline-flex items-center h-9 px-3 rounded-md bg-[#FDB913] shadow-sm" data-testid="payment-tmoney" title="T-Money">
        <span className="font-display font-extrabold text-[13px] text-[#1A1A1A] tracking-tight">T-Money</span>
      </span>
      {/* Visa */}
      <span className="inline-flex items-center h-9 px-3 rounded-md bg-white shadow-sm" data-testid="payment-visa" title="Visa">
        <span className="font-display font-extrabold italic text-[15px] text-[#1A1F71] tracking-wider">VISA</span>
      </span>
      {/* Mastercard */}
      <span className="inline-flex items-center h-9 px-3 rounded-md bg-white shadow-sm gap-1.5" data-testid="payment-mastercard" title="Mastercard">
        <span className="relative w-7 h-5 inline-block">
          <span className="absolute left-0 top-0 w-5 h-5 rounded-full bg-[#EB001B]" />
          <span className="absolute right-0 top-0 w-5 h-5 rounded-full bg-[#F79E1B] opacity-90 mix-blend-multiply" />
        </span>
        <span className="text-[10px] font-bold text-slate-700 lowercase">mastercard</span>
      </span>
    </div>
  );
}

function SocialRow({ links }) {
  if (!links || !links.length) return null;
  return (
    <div className="flex items-center gap-2.5" data-testid="footer-socials">
      {links.map((s, i) => {
        const Icon = ICONS[(s.platform || "").toLowerCase()] || Globe;
        return (
          <a key={i} href={s.url} target="_blank" rel="noreferrer" aria-label={s.platform}
             data-testid={`social-${(s.platform || "link").toLowerCase()}`}
             className="w-9 h-9 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-[#C85A32] transition-colors">
            <Icon className="w-4 h-4" />
          </a>
        );
      })}
    </div>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Veuillez accepter la politique de confidentialité");
    setBusy(true);
    try { await api.post("/newsletter/subscribe", { email }); toast.success("Merci ! Vous êtes abonné(e) à la newsletter TOGOBATI."); setEmail(""); setConsent(false); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="max-w-xl" data-testid="newsletter-form">
      <h3 className="font-display text-lg font-bold text-white">Nouveau sur TOGOBATI ?</h3>
      <p className="text-xs text-slate-400 mt-1 leading-relaxed">Abonnez-vous à notre newsletter pour recevoir nos meilleures offres sur les matériaux de construction. Vous pouvez vous désabonner à tout moment.</p>
      <label className="flex items-start gap-2 text-[11px] text-slate-400 mt-3">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="newsletter-consent" />
        <span>J'accepte la <Link to="/confidentialite" className="text-[#E08B00] hover:underline">Politique de confidentialité</Link> et je comprends que je peux me désabonner à tout moment.</span>
      </label>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Entrez votre adresse e-mail"
            className="w-full bg-white text-slate-800 rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/50" data-testid="newsletter-email" />
        </div>
        <button disabled={busy} className="bg-[#C85A32] hover:bg-[#A94520] text-white font-bold px-6 py-2.5 rounded-lg inline-flex items-center justify-center gap-2 transition disabled:opacity-50 whitespace-nowrap" data-testid="newsletter-submit">
          <Send className="w-4 h-4" /> {busy ? "..." : "S'abonner"}
        </button>
      </div>
    </form>
  );
}

export default function SiteFooter() {
  const [cols, setCols] = useState([]);
  const [settings, setSettings] = useState(null);
  const [company, setCompany] = useState(null);
  useEffect(() => {
    api.get("/footer/columns").then((r) => setCols(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const linkFor = (l) => l.href || `/pages/${l.slug}`;
  const year = new Date().getFullYear();
  const copyright = (settings?.copyright || "© {year} TOGOBATI").replace("{year}", year);
  const socials = settings?.social_links || [];
  const wa = settings?.whatsapp || {};

  return (
    <footer className="mt-10 text-slate-300 pb-20 md:pb-0" data-testid="client-footer">
      {/* ===== Bande newsletter ===== */}
      <div className="bg-[#1E293B]">
        <div className="max-w-6xl mx-auto px-4 py-8 sm:py-10 grid md:grid-cols-2 gap-8 items-start">
          <Newsletter />
          <div className="md:justify-self-end" data-testid="footer-poche">
            <h3 className="font-display text-lg font-bold text-white">TOGOBATI à portée de main</h3>
            <p className="text-xs text-slate-400 mt-1">Installez notre application ou contactez-nous directement.</p>
            {wa.enabled !== false && wa.number && (
              <a href={`https://wa.me/${(wa.number || "").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer"
                 className="mt-3 inline-flex items-center gap-2 bg-[#25D366] hover:brightness-110 text-white font-bold px-4 py-2.5 rounded-lg transition" data-testid="footer-whatsapp">
                <MessageCircle className="w-4 h-4" /> Discuter sur WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ===== Bande principale ===== */}
      <div className="bg-[#0F172A]">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="col-span-2 lg:col-span-1">
              <img src={logo} alt="TOGOBATI" className="h-9 w-auto brightness-0 invert mb-3" />
              <p className="text-xs text-slate-400 leading-relaxed">Matériaux de construction & services d'installation à Lomé, Togo.</p>
              <Link to="/blog" className="inline-block text-xs font-semibold text-[#E08B00] mt-2 hover:underline" data-testid="footer-blog-link">Blog TOGOBATI →</Link>
            </div>
            {cols.map((c) => (
              <div key={c.id} data-testid={`footer-col-${c.key}`}>
                <h4 className="text-xs font-bold tracking-wider text-white mb-3 uppercase">{c.label}</h4>
                <ul className="space-y-2">
                  {(c.links || []).map((l) => (
                    <li key={l.slug}>
                      <Link to={linkFor(l)} className="text-sm text-slate-400 hover:text-[#E08B00] transition" data-testid={`footer-link-${l.slug}`}>{l.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contact */}
          {company && (company.phone || company.address || company.email) && (
            <div className="mt-10 pt-6 border-t border-white/10" data-testid="footer-contact">
              <h4 className="text-xs font-bold tracking-wider text-white mb-3 uppercase">Contactez-nous</h4>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-6 text-sm text-slate-400">
                {company.phone && <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4 text-[#E08B00]" /> {company.phone}</span>}
                {company.address && <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#E08B00]" /> {company.address}</span>}
                {company.email && <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4 text-[#E08B00]" /> {company.email}</span>}
              </div>
            </div>
          )}

          {/* Réseaux + paiement */}
          <div className="mt-8 pt-6 border-t border-white/10 grid md:grid-cols-2 gap-8">
            <div data-testid="footer-follow">
              <h4 className="text-xs font-bold tracking-wider text-white mb-3 uppercase">Retrouvez-nous sur</h4>
              {socials.length ? <SocialRow links={socials} /> : <p className="text-xs text-slate-500">Bientôt sur les réseaux sociaux.</p>}
            </div>
            <div data-testid="footer-payments">
              <h4 className="text-xs font-bold tracking-wider text-white mb-3 uppercase">Modes de paiement & livraison</h4>
              <PaymentLogos />
              <div className="flex flex-wrap gap-2 mt-2">
                {PAYMENTS.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-slate-800 text-slate-300 text-xs font-semibold px-3 py-1.5 rounded-lg" data-testid={`payment-extra-${i}`}>
                    <p.icon className="w-4 h-4 text-[#E08B00]" /> {p.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== Bas de page ===== */}
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-xs text-slate-500" data-testid="footer-copyright">{copyright}</p>
            <div className="flex items-center gap-4 text-[11px] font-bold tracking-wider text-slate-400">
              <span className="inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5 text-[#E08B00]" /> TOGOBATI DELIVERY</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
